/**
 * Maintenance Intelligence Module Configuration
 *
 * Maintenance planning, cost optimization, parts prediction, and ROI analysis.
 * DISABLED for Financial and Healthcare industries.
 * - Financial: No physical maintenance (software/systems only)
 * - Healthcare: Patient-focused industry, not equipment maintenance
 */

import { ModuleConfig } from './moduleTypes'

export const maintenanceConfig: ModuleConfig = {
  id: 'maintenance',
  name: 'Maintenance Intelligence',
  description: 'Proactive maintenance planning and cost optimization',
  icon: 'Wrench',
  basePath: '/maintenance',
  // Note: Financial and Healthcare industries excluded
  // - Financial: No physical maintenance (software/systems only)
  // - Healthcare: Patient-focused industry, not equipment maintenance
  enabledIndustries: ['manufacturing', 'oil_gas', 'energy', 'transportation', 'mining', 'chemicals', 'food_beverage', 'pharmaceuticals', 'aerospace', 'metals', 'iot_telecom', 'utilities', 'pulp_paper', 'automotive', 'datacenter', 'facility_management',
    // Phase 4 — non-industrial + medical-grade verticals (2026-06-27)
    'aquaculture_lobster', 'aquaculture_salmon', 'aquaculture_shrimp',
    'biosensor_ide_wearable', 'biosensor_continuous_glucose', 'biosensor_oximeter_fleet',
    'wearable_etd'],

  subPages: [
    {
      id: 'overview',
      path: '',
      name: 'Maintenance Overview',
      icon: 'LayoutDashboard',
      description: 'Summary of maintenance activities and priorities',
      component: 'MaintenanceOverview',
      requiredPermissions: ['maintenance:read'],
      position: 1,
      industryLabels: {
        healthcare: 'Service Overview',
        transportation: 'Fleet Service Overview',
        iot_telecom: 'SIM Management Overview',
      },
    },
    {
      id: 'schedule',
      path: 'schedule',
      name: 'Maintenance Schedule',
      icon: 'Calendar',
      description: 'Upcoming maintenance calendar and planning',
      component: 'MaintenanceSchedule',
      requiredPermissions: ['maintenance:read'],
      position: 2,
      industryLabels: {
        healthcare: 'Service Schedule',
        transportation: 'Fleet Service Schedule',
        iot_telecom: 'OTA Update Schedule',
      },
    },
    {
      id: 'cost',
      path: 'cost',
      name: 'Cost Calculator',
      icon: 'Calculator',
      description: 'Maintenance cost estimation and comparison',
      component: 'CostCalculator',
      requiredPermissions: ['maintenance:read'],
      position: 3,
      industryLabels: {
        healthcare: 'Service Cost Calculator',
        iot_telecom: 'Connectivity Cost Calculator',
      },
    },
    {
      id: 'parts',
      path: 'parts',
      name: 'Parts Prediction',
      icon: 'Package',
      description: 'Predicted parts requirements and procurement',
      component: 'PartsPrediction',
      requiredPermissions: ['maintenance:read'],
      position: 4,
      industryLabels: {
        healthcare: 'Parts & Consumables',
        iot_telecom: 'Hardware Prediction',
      },
    },
    {
      id: 'roi',
      path: 'roi',
      name: 'ROI Analysis',
      icon: 'PiggyBank',
      description: 'Return on investment for maintenance activities',
      component: 'MaintenanceROI',
      requiredPermissions: ['analytics:read'],
      position: 5,
    },
    {
      id: 'work-orders',
      path: 'work-orders',
      name: 'Work Orders',
      icon: 'ClipboardList',
      description: 'Work order management and tracking',
      component: 'WorkOrderIntegration',
      requiredPermissions: ['maintenance:write'],
      position: 6,
      industryLabels: {
        healthcare: 'Service Orders',
        transportation: 'Service Tickets',
        iot_telecom: 'Support Tickets',
      },
    },
    {
      id: 'efficiency',
      path: 'efficiency',
      name: 'Efficiency Trends',
      icon: 'TrendingUp',
      description: 'Maintenance efficiency metrics over time',
      component: 'MaintenanceEfficiency',
      requiredPermissions: ['analytics:read'],
      position: 7,
    },
  ],

  workflows: [
    {
      id: 'maintenance-planning',
      name: 'Maintenance Planning',
      description: 'Systematic maintenance planning workflow',
      icon: 'Calendar',
      steps: [
        {
          id: 'review-priorities',
          name: 'Review Priorities',
          description: 'Identify high-priority maintenance needs',
          navigateTo: 'overview',
          icon: 'AlertTriangle',
          estimatedDuration: 5,
        },
        {
          id: 'estimate-costs',
          name: 'Estimate Costs',
          description: 'Calculate maintenance costs and compare options',
          navigateTo: 'cost',
          icon: 'Calculator',
          estimatedDuration: 10,
        },
        {
          id: 'check-parts',
          name: 'Check Parts Availability',
          description: 'Verify required parts are available',
          navigateTo: 'parts',
          icon: 'Package',
          estimatedDuration: 5,
        },
        {
          id: 'schedule-work',
          name: 'Schedule Work',
          description: 'Create and schedule work orders',
          navigateTo: 'schedule',
          icon: 'Calendar',
          estimatedDuration: 10,
        },
        {
          id: 'create-work-orders',
          name: 'Create Work Orders',
          description: 'Generate work orders for technicians',
          navigateTo: 'work-orders',
          icon: 'ClipboardList',
          estimatedDuration: 10,
        },
      ],
      triggerConditions: ['maintenanceDue', 'predictionTriggered'],
    },
    {
      id: 'cost-optimization',
      name: 'Cost Optimization',
      description: 'Optimize maintenance costs and ROI',
      icon: 'DollarSign',
      steps: [
        {
          id: 'analyze-costs',
          name: 'Analyze Current Costs',
          description: 'Review maintenance cost breakdown',
          navigateTo: 'cost',
          icon: 'PieChart',
          estimatedDuration: 10,
        },
        {
          id: 'review-efficiency',
          name: 'Review Efficiency',
          description: 'Analyze maintenance efficiency trends',
          navigateTo: 'efficiency',
          icon: 'TrendingUp',
          estimatedDuration: 10,
        },
        {
          id: 'calculate-roi',
          name: 'Calculate ROI',
          description: 'Assess return on maintenance investments',
          navigateTo: 'roi',
          icon: 'PiggyBank',
          estimatedDuration: 10,
        },
        {
          id: 'optimize-schedule',
          name: 'Optimize Schedule',
          description: 'Adjust schedule for cost efficiency',
          navigateTo: 'schedule',
          icon: 'Calendar',
          estimatedDuration: 15,
        },
        {
          id: 'implement-changes',
          name: 'Implement Changes',
          description: 'Update work orders with optimizations',
          navigateTo: 'work-orders',
          icon: 'CheckSquare',
          estimatedDuration: 10,
        },
      ],
      triggerConditions: ['quarterlyReview', 'budgetPlanning'],
    },
  ],

  narratives: {
    sections: [
      {
        id: 'summary',
        name: 'Maintenance Summary',
        template: '{totalPending} {maintenanceTerm} actions pending. {criticalCount} critical, {scheduledCount} scheduled. Estimated total cost: {totalCost}.',
        order: 1,
        required: true,
      },
      {
        id: 'critical-actions',
        name: 'Critical Actions',
        template: '**Critical Actions Required:**\n{criticalActionsList}\nPotential savings from proactive action: {potentialSavings}.',
        order: 2,
        condition: 'hasCriticalActions',
      },
      {
        id: 'cost-analysis',
        name: 'Cost Analysis',
        template: 'Proactive cost: {proactiveCost}. Reactive cost if deferred: {reactiveCost}. ROI of proactive approach: {roiPercent}%.',
        order: 3,
      },
      {
        id: 'parts-alert',
        name: 'Parts Procurement Alert',
        template: '**Parts Alert:** {urgentPartsCount} parts needed urgently. Lead time concerns for: {leadTimeConcerns}.',
        order: 4,
        condition: 'hasUrgentParts',
      },
      {
        id: 'recommendations',
        name: 'Recommendations',
        template: '**Recommendations:**\n1. {recommendation1}\n2. {recommendation2}\n3. {recommendation3}',
        order: 5,
        required: true,
      },
    ],
    availableVariables: [
      'totalPending', 'maintenanceTerm', 'criticalCount', 'scheduledCount', 'totalCost',
      'criticalActionsList', 'potentialSavings',
      'proactiveCost', 'reactiveCost', 'roiPercent',
      'urgentPartsCount', 'leadTimeConcerns',
      'recommendation1', 'recommendation2', 'recommendation3',
    ],
  },

  industryOverrides: {
    // Healthcare excluded - patient-focused industry, not equipment maintenance
    transportation: {
      name: 'Fleet Service',
      description: 'Vehicle maintenance and service management',
      icon: 'Truck',
      additionalSubPages: [
        {
          id: 'inspections',
          path: 'inspections',
          name: 'Vehicle Inspections',
          icon: 'ClipboardCheck',
          description: 'Pre-trip and scheduled inspections',
          component: 'VehicleInspections',
          position: 8,
        },
        {
          id: 'recalls',
          path: 'recalls',
          name: 'Recall Management',
          icon: 'AlertOctagon',
          description: 'Vehicle recall tracking and compliance',
          component: 'RecallManagement',
          position: 9,
        },
      ],
    },
    iot_telecom: {
      name: 'SIM Management',
      description: 'SIM lifecycle management with OTA updates and profile switching',
      icon: 'Sim',
      additionalSubPages: [
        {
          id: 'profile-management',
          path: 'profiles',
          name: 'Profile Management',
          icon: 'Layers',
          description: 'eUICC profile provisioning and Multi-IMSI management',
          component: 'ProfileManagement',
          position: 8,
        },
        {
          id: 'ota-updates',
          path: 'ota',
          name: 'OTA Updates',
          icon: 'RefreshCw',
          description: 'Over-the-air firmware and profile updates',
          component: 'OTAUpdates',
          position: 9,
        },
        {
          id: 'carrier-switch',
          path: 'carrier-switch',
          name: 'Carrier Switch Queue',
          icon: 'ArrowRightLeft',
          description: 'Pending and scheduled carrier profile switches',
          component: 'CarrierSwitchQueue',
          position: 10,
        },
        {
          id: 'sim-lifecycle',
          path: 'lifecycle',
          name: 'SIM Lifecycle',
          icon: 'History',
          description: 'SIM activation, suspension, and decommissioning',
          component: 'SimLifecycle',
          position: 11,
        },
        {
          id: 'compliance-audit',
          path: 'compliance',
          name: 'Regulatory Compliance',
          icon: 'Shield',
          description: 'Track regulatory compliance across regions',
          component: 'RegulatoryCompliance',
          position: 12,
        },
        {
          id: 'antenna-optimization',
          path: 'antenna',
          name: 'Antenna Optimization',
          icon: 'Antenna',
          description: 'Field antenna positioning and optimization',
          component: 'AntennaOptimization',
          position: 13,
        },
        {
          id: 'gateway-firmware',
          path: 'gateway',
          name: 'Gateway Management',
          icon: 'Server',
          description: 'IoT gateway firmware and configuration',
          component: 'GatewayManagement',
          position: 14,
        },
      ],
      additionalWorkflows: [
        {
          id: 'bulk-profile-switch',
          name: 'Bulk Profile Switch',
          description: 'Switch carrier profiles for multiple SIMs',
          icon: 'ArrowRightLeft',
          steps: [
            {
              id: 'select-sims',
              name: 'Select SIMs',
              description: 'Choose SIMs for profile switch',
              navigateTo: 'overview',
              icon: 'CheckSquare',
              estimatedDuration: 3,
            },
            {
              id: 'review-profiles',
              name: 'Review Target Profiles',
              description: 'Verify target carrier profiles',
              navigateTo: 'profile-management',
              icon: 'Layers',
              estimatedDuration: 5,
            },
            {
              id: 'schedule-switch',
              name: 'Schedule Switch',
              description: 'Set optimal time window for switch',
              navigateTo: 'carrier-switch',
              icon: 'Calendar',
              estimatedDuration: 5,
            },
            {
              id: 'execute-switch',
              name: 'Execute Switch',
              description: 'Initiate OTA profile switch',
              navigateTo: 'ota-updates',
              icon: 'Play',
              estimatedDuration: 3,
            },
            {
              id: 'verify-success',
              name: 'Verify Success',
              description: 'Confirm all SIMs switched successfully',
              icon: 'CheckCircle',
              estimatedDuration: 5,
            },
          ],
          triggerConditions: ['carrierOptimization', 'costReduction', 'contractMigration'],
        },
        {
          id: 'sim-provisioning',
          name: 'New SIM Provisioning',
          description: 'Provision and activate new SIMs in the fleet',
          icon: 'PlusCircle',
          steps: [
            {
              id: 'register-sim',
              name: 'Register SIM',
              description: 'Add SIM to inventory with ICCID',
              navigateTo: 'sim-lifecycle',
              icon: 'Plus',
              estimatedDuration: 2,
            },
            {
              id: 'configure-profiles',
              name: 'Configure Profiles',
              description: 'Set up Multi-IMSI carrier profiles',
              navigateTo: 'profile-management',
              icon: 'Layers',
              estimatedDuration: 5,
            },
            {
              id: 'push-ota',
              name: 'Push OTA Configuration',
              description: 'Send configuration via OTA update',
              navigateTo: 'ota-updates',
              icon: 'RefreshCw',
              estimatedDuration: 3,
            },
            {
              id: 'verify-compliance',
              name: 'Verify Compliance',
              description: 'Ensure regulatory compliance for region',
              navigateTo: 'compliance-audit',
              icon: 'Shield',
              estimatedDuration: 3,
            },
            {
              id: 'activate',
              name: 'Activate SIM',
              description: 'Activate and verify connectivity',
              icon: 'Power',
              estimatedDuration: 2,
            },
          ],
          triggerConditions: ['newDeployment', 'simReplacement', 'fleetExpansion'],
        },
        {
          id: 'gateway-update',
          name: 'Gateway Firmware Update',
          description: 'Update IoT gateway firmware fleet-wide',
          icon: 'Server',
          steps: [
            {
              id: 'select-gateways',
              name: 'Select Gateways',
              description: 'Choose gateways for update',
              navigateTo: 'gateway-firmware',
              icon: 'CheckSquare',
              estimatedDuration: 2,
            },
            {
              id: 'schedule-window',
              name: 'Schedule Update Window',
              description: 'Set maintenance window for update',
              navigateTo: 'schedule',
              icon: 'Calendar',
              estimatedDuration: 3,
            },
            {
              id: 'push-update',
              name: 'Push Firmware Update',
              description: 'Initiate OTA firmware update',
              navigateTo: 'ota-updates',
              icon: 'RefreshCw',
              estimatedDuration: 5,
            },
            {
              id: 'verify-update',
              name: 'Verify Update Success',
              description: 'Confirm all gateways updated successfully',
              icon: 'CheckCircle',
              estimatedDuration: 5,
            },
          ],
          triggerConditions: ['securityPatch', 'featureUpdate', 'bugFix'],
        },
      ],
    },
    datacenter: {
      name: 'Infrastructure Maintenance',
      description: 'Server hardware, cooling system, and UPS maintenance management',
      icon: 'Server',
    },
  },

  requiredPermissions: ['maintenance:read'],
}
